import React, {useMemo} from "react";
import {Box, Card, Chip, makeStyles, Tooltip, Typography} from "@material-ui/core";
import {CheckCircle, Error, NotInterested, Warning} from "@material-ui/icons";
import {useTranslation} from "react-i18next";
import DataTable from "react-data-table-component";
import {InfoBox} from "components";
import {DeliveryCsvAnalysisSummary} from "api/deliveryCsvAnalysis";

const useStyles = makeStyles(() => ({
  card: {
    overflow: "auto",
  },
  statusChip: {
    fontSize: "12px",
  },
  successChip: {
    backgroundColor: "rgba(76, 175, 80, 0.1)",
    color: "#4caf50",
  },
  warningChip: {
    backgroundColor: "rgba(255, 152, 0, 0.1)",
    color: "#ff9800",
  },
  errorChip: {
    backgroundColor: "rgba(244, 67, 54, 0.1)",
    color: "#f44336",
  },
  skippedChip: {
    backgroundColor: "rgba(158, 158, 158, 0.1)",
    color: "#9e9e9e",
  },
  iconSmall: {
    fontSize: "14px",
    marginRight: "4px",
  },
  infoIcon: {
    width: "16px",
    height: "16px",
  },
  infoText: {
    fontSize: "12px",
    color: "#666",
  },
  infoLabel: {
    fontSize: "11px",
    color: "#999",
  },
  nameCell: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  moreText: {
    fontSize: "12px",
    color: "#98A5AE",
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
  },
  moreIcon: {
    width: "14px",
    height: "14px",
    marginLeft: "2px",
  },
}));

interface AnalysisResultsTableProps {
  analyses: DeliveryCsvAnalysisSummary[];
  onRowClick?: (file: DeliveryCsvAnalysisSummary) => void;
  selectedFileId?: string | null;
}

export const AnalysisResultsTable = (props: AnalysisResultsTableProps) => {
  const { analyses, onRowClick, selectedFileId } = props;
  const classes = useStyles();
  const { t } = useTranslation();

  const renderNameList = (names: string[] | undefined) => {
    if (!names || names.length === 0) return "-";
    if (names.length === 1) return names[0];

    const remaining = names.slice(1);
    return (
      <Box className={classes.nameCell}>
        <span>{names[0]}</span>
        <span className={classes.moreText}>
          +{remaining.length}
          <InfoBox
            className={classes.moreIcon}
            content={
              <Typography className={classes.infoText}>
                {remaining.join(", ")}
              </Typography>
            }
          />
        </span>
      </Box>
    );
  };

  const columns = useMemo(() => [
    {
      name: "",
      cell: (row: DeliveryCsvAnalysisSummary) => (
        <InfoBox
          className={classes.infoIcon}
          content={
            <Box display="flex" flexDirection="column" gap="4px">
              <Box>
                <Typography className={classes.infoLabel}>{t("received_data_analysis_col_file")}</Typography>
                <Typography className={classes.infoText}>{row.fileName}</Typography>
              </Box>
              <Box>
                <Typography className={classes.infoLabel}>{t("received_data_analysis_importer")}</Typography>
                <Typography className={classes.infoText}>{row.importerName}</Typography>
              </Box>
            </Box>
          }
        />
      ),
      width: "50px",
    },
    {
      name: t("received_data_analysis_operators_col"),
      cell: (row: DeliveryCsvAnalysisSummary) => renderNameList(row.operatorNames),
      width: "180px",
    },
    {
      name: t("received_data_analysis_carriers_col"),
      cell: (row: DeliveryCsvAnalysisSummary) => renderNameList(row.carrierNames),
      width: "180px",
    },
    {
      name: t("received_data_analysis_col_period"),
      selector: (row: DeliveryCsvAnalysisSummary) => row.period,
      width: "100px",
    },
    {
      name: t("received_data_analysis_col_status"),
      cell: (row: DeliveryCsvAnalysisSummary) => <StatusChip status={row.status} tolerateErrors={row.tolerateErrors} />,
      width: "120px",
    },
    {
      name: t("received_data_analysis_col_tours"),
      selector: (row: DeliveryCsvAnalysisSummary) => row.nbTours,
      right: true,
      width: "100px",
    },
    {
      name: t("received_data_analysis_col_deliveries"),
      selector: (row: DeliveryCsvAnalysisSummary) => row.totalDeliveries,
      right: true,
      width: "120px",
    },
    {
      name: t("received_data_analysis_col_packages"),
      selector: (row: DeliveryCsvAnalysisSummary) => row.totalPackages,
      right: true,
      width: "100px",
    },
    {
      name: t("received_data_analysis_col_avg_approach"),
      cell: (row: DeliveryCsvAnalysisSummary) => `${row.averageApproachDistance.toFixed(0)}m`,
      right: true,
      width: "140px",
    },
  ], [t, classes]);

  const conditionalRowStyles = [
    {
      when: (row: DeliveryCsvAnalysisSummary) => row.id === selectedFileId,
      style: {
        backgroundColor: "rgba(60, 120, 216, 0.1)",
      },
    },
  ];

  return (
    <Card className={classes.card}>
      <DataTable
        columns={columns}
        data={analyses}
        pagination
        paginationPerPage={10}
        paginationRowsPerPageOptions={[10, 20, 50, 100]}
        noHeader
        theme="colisactiv"
        noDataComponent={<p style={{ padding: "20px" }}>{t("received_data_analysis_no_data")}</p>}
        onRowClicked={onRowClick}
        pointerOnHover={!!onRowClick}
        highlightOnHover={!!onRowClick}
        conditionalRowStyles={conditionalRowStyles}
      />
    </Card>
  );
};

interface StatusChipProps {
  status: string;
  tolerateErrors?: boolean;
}

const StatusChip = (props: StatusChipProps) => {
  const { status, tolerateErrors } = props;
  const classes = useStyles();
  const { t } = useTranslation();

  switch (status) {
    case "SUCCESS":
      return (
        <Chip
          icon={<CheckCircle className={classes.iconSmall} />}
          label="OK"
          size="small"
          className={`${classes.statusChip} ${classes.successChip}`}
        />
      );
    case "WARNING":
      return (
        <Tooltip title={t(tolerateErrors
          ? "received_data_analysis_warning_imported_tooltip"
          : "received_data_analysis_warning_low_avg_tooltip"
        )}>
          <Chip
            icon={<Warning className={classes.iconSmall} />}
            label="Alerte"
            size="small"
            className={`${classes.statusChip} ${classes.warningChip}`}
          />
        </Tooltip>
      );
    case "ERROR":
      return (
        <Chip
          icon={<Error className={classes.iconSmall} />}
          label="Erreur"
          size="small"
          className={`${classes.statusChip} ${classes.errorChip}`}
        />
      );
    case "SKIPPED":
      return (
        <Tooltip title={t("received_data_analysis_skipped_tooltip")}>
          <Chip
            icon={<NotInterested className={classes.iconSmall} />}
            label="Ignoré"
            size="small"
            className={`${classes.statusChip} ${classes.skippedChip}`}
          />
        </Tooltip>
      );
    default:
      return <Chip label={status} size="small" className={classes.statusChip} />;
  }
};
